/**
 * Print Opdrachtbon as one aligned A4 page (table layout — reliable in print).
 * No CSS transform/scale (that caused overlapping text in print preview).
 */

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const text = (value) => escapeHtml(value).trim();

const jaNee = (value) => {
  const jaOn = value === true ? ' on' : '';
  const neeOn = value === false ? ' on' : '';
  return `<span class="jn"><span class="opt${jaOn}">JA</span><span class="sep">/</span><span class="opt${neeOn}">NEE</span></span>`;
};

const val = (value, sizeClass = '') => {
  const t = text(value);
  const cls = ['val', sizeClass].filter(Boolean).join(' ');
  const blankCls = ['blank', sizeClass].filter(Boolean).join(' ');
  return t
    ? `<span class="${cls}">${t}</span>`
    : `<span class="${blankCls}">&nbsp;</span>`;
};

const line = (label, rightHtml) =>
  `<tr class="line"><td class="lab">${escapeHtml(label)}</td><td class="col">:</td><td class="fld">${rightHtml}</td></tr>`;

const publicImageUrl = (path) => {
  const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const buildOpdrachtbonPrintHtml = (data = {}, { quoteNumber = '' } = {}) => {
  const d = data || {};
  const logoSrc = publicImageUrl('/images/vmk-kozijnen-logo.png');
  const title = quoteNumber
    ? `Opdrachtbon ${quoteNumber}`
    : 'Opdrachtbon / checklist';

  return `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 9pt;
      line-height: 1.25;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    table { border-collapse: collapse; width: 100%; }
    td { vertical-align: top; }
    .sheet { width: 100%; }
    .block { width: 100%; margin: 0 0 5px; }
    .block:last-child { margin-bottom: 0; }

    .header td { vertical-align: middle; }
    .logo { height: 40px; width: auto; max-width: 180px; display: block; }
    .quote-no { font-size: 8pt; color: #555; margin-top: 3px; }
    .company { text-align: right; font-size: 7.5pt; line-height: 1.3; color: #444; }
    .company strong { color: #111; font-size: 8.5pt; }

    .filled {
      margin: 8px 0 0;
      font-weight: 700;
      font-size: 9.5pt;
    }
    .filled .blank, .filled .val {
      display: inline-block;
      min-width: 180px;
      border-bottom: 1px dotted #5B8DEF;
      margin-left: 6px;
      font-weight: 400;
      padding: 0 4px;
    }

    .panel {
      border: 2px solid #5B2C8A;
      padding: 7px 9px;
      width: 100%;
    }
    /* Two equal-height columns: left (client + Extra opties) | right (Montage + Til) */
    .main-cols {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      align-items: stretch;
    }
    .col-stack {
      display: flex;
      flex-direction: column;
      gap: 5px;
      min-height: 100%;
      height: 100%;
    }
    .col-stack > .panel {
      flex: 0 0 auto;
    }
    .col-stack > .panel.fill {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .two > tbody > tr > td,
    .two > tr > td {
      width: 50%;
      padding: 0;
      vertical-align: top;
    }
    .two > tbody > tr > td:first-child,
    .two > tr > td:first-child { padding-right: 4px; }
    .two > tbody > tr > td:last-child,
    .two > tr > td:last-child { padding-left: 4px; }

    .title {
      color: #5B2C8A;
      font-weight: 700;
      font-size: 9.5pt;
      margin: 0 0 4px;
      padding: 0;
    }
    .title.sub { margin-top: 7px; }

    .fields { width: 100%; }
    .line td { padding: 2px 0; vertical-align: baseline; }
    .lab {
      width: 1%;
      white-space: nowrap;
      font-weight: 600;
      padding-right: 4px !important;
    }
    .col {
      width: 1%;
      color: #666;
      padding-right: 4px !important;
    }
    .fld { width: 99%; }
    .val, .blank {
      display: inline-block;
      border-bottom: 1px dotted #5B8DEF;
      min-width: 2.5em;
      padding: 0 3px 1px;
      min-height: 1em;
      vertical-align: baseline;
    }
    .fld > .blank:only-child { width: 100%; }
    .w-sm { min-width: 2.8em !important; max-width: 4em; }
    .w-md { min-width: 4em !important; max-width: 6.5em; }
    .phone-pair {
      display: flex;
      align-items: baseline;
      width: 100%;
      gap: 0;
    }
    .phone-pair .val,
    .phone-pair .blank {
      flex: 1 1 0;
      min-width: 0 !important;
      max-width: none !important;
      width: auto;
    }
    .phone-sep {
      display: inline-block;
      margin: 0 6px;
      font-weight: 700;
      color: #555;
      flex: 0 0 auto;
    }
    .muted { color: #666; font-size: 8pt; margin: 0 3px; white-space: nowrap; }

    .jn { font-weight: 700; font-size: 8pt; white-space: nowrap; margin-right: 4px; }
    .jn .sep { margin: 0 2px; color: #888; font-weight: 400; }
    .jn .opt.on {
      text-decoration: underline;
      text-underline-offset: 2px;
      color: #5B2C8A;
    }

    /* Signature / notes row — taller boxes, no flex page tricks */
    .akkoord-row {
      display: flex;
      align-items: stretch;
      gap: 8px;
      min-height: 52mm;
    }
    .akkoord-row > .panel.tall {
      flex: 1 1 0;
      min-height: 52mm;
      display: flex;
      flex-direction: column;
    }
    .sign-label {
      font-weight: 600;
      margin-top: 8px;
    }
    .sign-line {
      flex: 1 1 auto;
      min-height: 16mm;
      border-bottom: 1px dotted #5B8DEF;
      margin: 4px 0 10px;
    }
    .notes {
      flex: 1 1 auto;
      border: 1px dotted #5B8DEF;
      min-height: 36mm;
      padding: 6px;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .delivery {
      margin-top: 6px;
      font-weight: 700;
    }
    .delivery .blank, .delivery .val {
      display: inline-block;
      min-width: 140px;
      border-bottom: 1px dotted #5B8DEF;
      margin-left: 6px;
      font-weight: 400;
      padding: 0 4px;
    }
    .legal {
      margin: 6px 0 0;
      font-size: 6.5pt;
      color: #555;
      line-height: 1.3;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="block">
      <table class="header">
        <tr>
          <td>
            <img class="logo" src="${escapeHtml(logoSrc)}" alt="VMK Kozijnen" />
            ${quoteNumber ? `<div class="quote-no">${escapeHtml(quoteNumber)}</div>` : ''}
          </td>
          <td class="company">
            <strong>VMK Kozijnen</strong><br/>
            Binnenhavenstraat 41<br/>
            7553 GH Hengelo (ov)<br/>
            info@vmkkozijnen.nl<br/>
            074 234 03 33<br/>
            KVK | 81936974<br/>
            BTW | NL862276652B01
          </td>
        </tr>
      </table>
      <div class="filled">Checklist ingevuld door:${val(d.filledBy)}</div>
    </div>

    <div class="block">
      <div class="main-cols">
        <div class="col-stack">
          <div class="panel">
            <table class="fields">
              ${line('Datum', val(d.datum))}
              ${line('Naam', val(d.naam))}
              ${line('Adres', val(d.adres))}
              ${line('Postcode', val(d.postcode))}
              ${line('Woonplaats', val(d.woonplaats))}
              ${line(
                'Telefoon',
                `<span class="phone-pair">${val(d.telefoon)}<span class="phone-sep">|</span>${val(d.telefoon2)}</span>`,
              )}
              ${line('Mail', val(d.mail))}
            </table>
            <div class="title sub">Technische specificatie</div>
            <table class="fields">
              ${line('Merk', val(d.merk))}
              ${line('Verbinding', val(d.verbinding))}
              ${line('Profiel', val(d.profiel))}
            </table>
          </div>
          <div class="panel fill">
            <div class="title">Extra opties</div>
            <table class="fields">
              ${line(
                'Klik hor op kleur en maat',
                `${jaNee(d.klikHor)}<span class="muted">aantal</span>${val(d.klikHorAantal, 'w-sm')}<span class="muted">kleur</span>${val(d.klikHorKleur, 'w-md')}`,
              )}
              ${line(
                'Screen elektrisch bediend',
                `${jaNee(d.screen)}<span class="muted">aantal</span>${val(d.screenAantal, 'w-sm')}<span class="muted">kleur</span>${val(d.screenKleur, 'w-md')}`,
              )}
              ${line(
                'Rolluik elektrisch bediend',
                `${jaNee(d.rolluik)}<span class="muted">aantal</span>${val(d.rolluikAantal, 'w-sm')}<span class="muted">kleur</span>${val(d.rolluikKleur, 'w-md')}`,
              )}
              ${line(
                'Dakraam',
                `${jaNee(d.dakraam)}<span class="muted">aantal</span>${val(d.dakraamAantal, 'w-sm')}<span class="muted">kleur</span>${val(d.dakraamKleur, 'w-md')}`,
              )}
            </table>
          </div>
        </div>
        <div class="col-stack">
          <div class="panel">
            <div class="title">Montage</div>
            <table class="fields">
              ${line('Montage', jaNee(d.montage))}
              ${line('Aantal kozijnen', val(d.aantalKozijnen))}
              ${line('Aantal deuren', val(d.aantalDeuren))}
              ${line('Type voordeur', val(d.typeVoordeur))}
              ${line(
                'Sloopwerkzaamheden',
                `${jaNee(d.sloopwerkzaamheden)}${val(d.sloopM2, 'w-sm')}<span class="muted">m²</span>`,
              )}
              ${line('Lateien aanbrengen', jaNee(d.lateien))}
              ${line('Container', jaNee(d.container))}
            </table>
            <div class="title sub">Montage</div>
            <table class="fields">
              ${line('Afwerking binnenzijde', jaNee(d.afwerkingBinnen))}
              ${line('Afwerking buitenzijde', jaNee(d.afwerkingBuiten))}
              ${line('Vensterbank binnenzijde', jaNee(d.vensterbankBinnen))}
            </table>
            <div class="title sub">Uitvoering</div>
            <table class="fields">
              ${line(
                'Kleur kozijn kader',
                `${val(d.kleurKader, 'w-md')}<span class="muted">vleugel</span>${val(d.kleurVleugel, 'w-md')}`,
              )}
              ${line('Binnenzijde', val(d.binnenzijde))}
              ${line('Type glas', val(d.typeGlas))}
            </table>
          </div>
          <div class="panel fill">
            <div class="title">Til- / hefondersteuning</div>
            <table class="fields">
              ${line(
                'Kraan',
                `${jaNee(d.kraan)}${val(d.kraanHoogte, 'w-sm')}<span class="muted">m hoog</span>`,
              )}
              ${line(
                'Til-assistentie',
                `${jaNee(d.tilAssistentie)}${val(d.tilHoogte, 'w-sm')}<span class="muted">m hoog</span>`,
              )}
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="block">
      <table>
        <tr>
          <td>
            <div class="panel">
              <div class="title">Woningafwerking</div>
              <table class="fields">
                ${line(
                  'Gevelbekleding',
                  `${jaNee(d.gevelbekleding)}${val(d.gevelbekledingM2, 'w-sm')}<span class="muted">m²</span>`,
                )}
                ${line(
                  'Boeidelen',
                  `${jaNee(d.boeidelen)}${val(d.boeidelenM2, 'w-sm')}<span class="muted">m²</span>`,
                )}
                ${line(
                  'Zetwerk',
                  `${jaNee(d.zetwerk)}${val(d.zetwerkM2, 'w-sm')}<span class="muted">m²</span>`,
                )}
                ${line(
                  'Dorpelstenen',
                  `${jaNee(d.dorpelstenen)}${val(d.dorpelstenenM, 'w-sm')}<span class="muted">m</span><span class="muted">kleur</span>${val(d.dorpelstenenKleur, 'w-md')}`,
                )}
              </table>
            </div>
          </td>
        </tr>
      </table>
    </div>

    <div class="block">
      <div class="akkoord-row">
        <div class="panel tall">
          <div class="title">Akkoord</div>
          <table class="fields">
            ${line('Getekend te', val(d.getekendTe))}
          </table>
          <div class="sign-label">Handtekening opdrachtgever :</div>
          <div class="sign-line">${text(d.handtekeningOpdrachtgever) || ''}</div>
          <div class="sign-label">Handtekening adviseur :</div>
          <div class="sign-line">${text(d.handtekeningAdviseur) || ''}</div>
        </div>
        <div class="panel tall">
          <div class="title">Opmerkingen</div>
          <div class="notes">${text(d.opmerkingen) || '&nbsp;'}</div>
        </div>
      </div>
    </div>

    <div class="block">
      <div class="delivery">Levering: Z.S.M. of${val(d.levering)}</div>
      <p class="legal">
        Op al onze aanbiedingen en overeenkomsten zijn onze algemene voorwaarden van toepassing.
        Deze zijn te vinden op onze website en worden op verzoek toegezonden. Door ondertekening
        van deze opdrachtbon verklaart de opdrachtgever akkoord te gaan met de gestelde voorwaarden
        en specificaties.
      </p>
    </div>
  </div>
</body>
</html>`;
};

export const printOpdrachtbon = (data, { quoteNumber = '' } = {}) => {
  if (typeof document === 'undefined') return false;

  const html = buildOpdrachtbonPrintHtml(data, { quoteNumber });
  const iframeId = 'opdrachtbon-print-frame';

  // Always recreate iframe so no stale CSS/transform remains
  const existing = document.getElementById(iframeId);
  if (existing) existing.remove();

  const iframe = document.createElement('iframe');
  iframe.id = iframeId;
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('title', 'Opdrachtbon print');
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDoc = frameWindow?.document;
  if (!frameWindow || !frameDoc) return false;

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const triggerPrint = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } catch (error) {
      console.warn('Opdrachtbon print failed', error);
    }
  };

  const imgs = Array.from(frameDoc.images || []);
  if (!imgs.length) {
    setTimeout(triggerPrint, 100);
    return true;
  }

  let pending = imgs.length;
  let printed = false;
  const done = () => {
    if (printed) return;
    printed = true;
    setTimeout(triggerPrint, 50);
  };
  const onOne = () => {
    pending -= 1;
    if (pending <= 0) done();
  };

  imgs.forEach((img) => {
    if (img.complete) onOne();
    else {
      img.addEventListener('load', onOne, { once: true });
      img.addEventListener('error', onOne, { once: true });
    }
  });
  setTimeout(done, 1200);

  return true;
};

export default printOpdrachtbon;
